import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { DataPromise } from '../../context/AxiosContext'
import {
    Field,
    makeStyles,
    mergeClasses,
    shorthands,
    Skeleton,
    SkeletonItem,
    Text,
    Title2,
    tokens,
} from '@fluentui/react-components'
import useEntity from '../../hooks/useEntity'
import { IFileDto } from '../../config/fileConfig'
import { PriceDto } from '../../config/finance/financeConfig'
import MoneyText from '../../components/Text/Currency/MoneyText'
import { parseNumber } from '../../helpers/numberHelper'
import useViewport, { Viewport } from '../../hooks/useViewport'

type Props = {
    entity: DataPromise<IFileDto>
}

export type FileFinanceMonitoringDto = {
    fileId: string
    outstandingHours: PriceDto
    outstandingTravels: PriceDto
    outstandingDisbursements: PriceDto
    outstandingSubTotal: PriceDto
    outstandingInvoices: PriceDto
    outstandingDeposits: PriceDto
    outstandingTotal: PriceDto
    billedHours: PriceDto
    billedTravels: PriceDto
    billedDisbursements: PriceDto
    billedSubTotal: PriceDto
    total: PriceDto
}

const useStyles = makeStyles({
    container: {
        display: 'grid',
        margin: tokens.spacingHorizontalM,
        gridTemplateColumns: '1fr 1fr',
        ...shorthands.gap(tokens.spacingHorizontalM, tokens.spacingVerticalM),
    },
    containerVertical: {
        display: 'grid',
        margin: tokens.spacingHorizontalM,
        gridTemplateColumns: '1fr',
    },
    skeletonContainer: {
        display: 'grid',
        margin: tokens.spacingHorizontalM,
        gridTemplateColumns: '30% 15% 30% 15%',
        ...shorthands.gap(tokens.spacingHorizontalL, tokens.spacingVerticalL),
    },
    field: {
        display: 'grid',
        gridTemplateColumns: '70% 15% 15%',
        ...shorthands.gap(tokens.spacingHorizontalM, tokens.spacingVerticalXS),
    },
    fieldTotal: {
        borderTopStyle: 'solid',
        borderTopColor: tokens.colorNeutralStroke2,
        borderTopWidth: tokens.strokeWidthThin,
    },
})

export default function FinanceMonitoringOverview(props: Props): ReactElement {
    const styles = useStyles()
    const { isViewport, viewport } = useViewport()

    const horizontal: boolean = useMemo(() => isViewport('>', Viewport.XL), [viewport])

    const [data] = useEntity<FileFinanceMonitoringDto>(
        () => ({ url: `/file/${props.entity.data?.id}/finance/monitoring` }),
        props.entity.data?.id ? false : null,
    )

    if (!data.loaded) {
        return (
            <Skeleton aria-label="Loading...">
                <div className={styles.skeletonContainer}>
                    <Title2>Financieel risico</Title2>
                    <Field className={styles.field} label={' '}>
                        &nbsp;
                    </Field>
                    <Title2>Omzet</Title2>
                    <Field className={styles.field} label={' '}>
                        &nbsp;
                    </Field>
                </div>
                <div className={styles.skeletonContainer}>
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                </div>
                <div className={styles.skeletonContainer}>
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                </div>
                <div className={styles.skeletonContainer}>
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                </div>
                <div className={styles.skeletonContainer}>
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                </div>
                <div className={styles.skeletonContainer}>
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                </div>
                <div className={styles.skeletonContainer}>
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                </div>
                <div className={styles.skeletonContainer}>
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                </div>
                <div className={styles.skeletonContainer}>
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                </div>
                <div className={styles.skeletonContainer}>
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                </div>
            </Skeleton>
        )
    }

    return (
        <div>
            <div className={horizontal ? styles.container : styles.containerVertical}>
                <div>
                    <Title2>Financieel risico</Title2>
                    <Field className={styles.field} label={'Nog te declareren honorarium (netto)'}>
                        <MoneyText
                            alpha={data.data.outstandingHours?.currency || 'EUR'}
                            amount={data.data.outstandingHours?.subtotal || '0.00'}
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                    <Field className={styles.field} label={'Nog te declareren verschotten (netto)'}>
                        <MoneyText
                            alpha={data.data.outstandingDisbursements?.currency || 'EUR'}
                            amount={data.data.outstandingDisbursements?.subtotal || '0.00'}
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                    <Field className={styles.field} label={'Nog te declareren reiskosten (netto)'}>
                        <MoneyText
                            alpha={data.data.outstandingTravels?.currency || 'EUR'}
                            amount={data.data.outstandingTravels?.subtotal || '0.00'}
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                    <Field
                        className={mergeClasses(styles.field, mergeClasses(styles.field, styles.fieldTotal))}
                        label={<strong>{'Subtotaal nog te declareren (netto)'}</strong>}
                    >
                        <MoneyText
                            alpha={data.data.outstandingSubTotal?.currency || 'EUR'}
                            amount={data.data.outstandingSubTotal?.subtotal || '0.00'}
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                    <Field className={styles.field} label={'BTW nog te declareren'}>
                        <MoneyText
                            alpha={data.data.outstandingSubTotal?.currency || 'EUR'}
                            amount={
                                data.data.outstandingSubTotal?.vat
                                    ?.map(price => parseNumber(price.value, 'en'))
                                    .reduce((a, b) => a + b, 0) || '0.00'
                            }
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                    <Field
                        className={mergeClasses(styles.field, styles.fieldTotal)}
                        label={<strong>{'Totaal nog te declareren (bruto)'}</strong>}
                    >
                        <MoneyText
                            alpha={data.data.outstandingSubTotal?.currency || 'EUR'}
                            amount={data.data.outstandingSubTotal?.total || '0.00'}
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                    <Field className={styles.field} label={'Openstaande declaraties (bruto)'}>
                        <MoneyText
                            alpha={data.data.outstandingInvoices?.currency || 'EUR'}
                            amount={data.data.outstandingInvoices?.total || '0.00'}
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                    <Field className={styles.field} label={'Nog te verrekenen voorschotnota’s (bruto)'}>
                        <MoneyText
                            alpha={data.data.outstandingDeposits?.currency || 'EUR'}
                            amount={data.data.outstandingDeposits?.total || '0.00'}
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                    <Field
                        className={mergeClasses(styles.field, styles.fieldTotal)}
                        label={<strong>{'Financieel risico incl. BTW (bruto)'}</strong>}
                    >
                        <MoneyText
                            alpha={data.data.outstandingTotal?.currency || 'EUR'}
                            amount={data.data.outstandingTotal?.total || '0.00'}
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                </div>
                <div>
                    <Title2>Omzet</Title2>
                    <Field className={styles.field} label={'Honorarium gedeclareerd (netto)'}>
                        <MoneyText
                            alpha={data.data.billedHours?.currency || 'EUR'}
                            amount={data.data.billedHours?.subtotal || '0.00'}
                            align={'spaceBetween'}
                        />
                    </Field>
                    <Field className={styles.field} label={'Verschotten gedeclareerd (netto)'}>
                        <MoneyText
                            alpha={data.data.billedDisbursements?.currency || 'EUR'}
                            amount={data.data.billedDisbursements?.subtotal || '0.00'}
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                    <Field className={styles.field} label={'Reiskosten gedeclareerd (netto)'}>
                        <MoneyText
                            alpha={data.data.billedTravels?.currency || 'EUR'}
                            amount={data.data.billedTravels?.subtotal || '0.00'}
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                    <Field
                        className={mergeClasses(styles.field, styles.fieldTotal)}
                        label={<strong>{'Totaal gedeclareerd (netto)'}</strong>}
                    >
                        <MoneyText
                            alpha={data.data.billedSubTotal?.currency || 'EUR'}
                            amount={data.data.billedSubTotal?.subtotal || '0.00'}
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                    <Field className={styles.field} label={'Nog te declareren honorarium (netto)'}>
                        <MoneyText
                            alpha={data.data.outstandingHours?.currency || 'EUR'}
                            amount={data.data.outstandingHours?.subtotal || '0.00'}
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                    <Field className={styles.field} label={'Nog te declareren verschotten (netto)'}>
                        <MoneyText
                            alpha={data.data.outstandingDisbursements?.currency || 'EUR'}
                            amount={data.data.outstandingDisbursements?.subtotal || '0.00'}
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                    <Field className={styles.field} label={'Nog te declareren reiskosten (netto)'}>
                        <MoneyText
                            alpha={data.data.outstandingTravels?.currency || 'EUR'}
                            amount={data.data.outstandingTravels?.subtotal || '0.00'}
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                    <Field
                        className={mergeClasses(styles.field, mergeClasses(styles.field, styles.fieldTotal))}
                        label={<strong>{'Subtotaal nog te declareren (netto)'}</strong>}
                    >
                        <MoneyText
                            alpha={data.data.outstandingSubTotal?.currency || 'EUR'}
                            amount={data.data.outstandingSubTotal?.subtotal || '0.00'}
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                    <Field
                        className={mergeClasses(styles.field, styles.fieldTotal)}
                        label={<strong>{'Gedeclareerd + nog te declareren (netto)'}</strong>}
                    >
                        <MoneyText
                            alpha={data.data.total?.currency || 'EUR'}
                            amount={data.data.total?.subtotal || '0.00'}
                            align={'spaceBetween'}
                        />
                        <Text>&nbsp;</Text>
                    </Field>
                </div>
            </div>
        </div>
    )
}
